<template>
  <div class="container-fluid pt-2">

    <!-- Header Row -->
    <div class="row">
      <div class="col-md-4 col-sm-12">
        <div class="text-left py-3">
          <a href="/discount-campaigns" class="btn btn-sm btn-outline-secondary mb-1">
            <i class="bi bi-arrow-left mr-1"></i> Campaigns
          </a>
          <div v-if="campaign">
            <h3 class="text-weight-900 mb-0 text-primary">{{ campaign.name }}</h3>
            <small class="text-muted">
              {{ campaign.starts_at | shortDate }} – {{ campaign.ends_at | shortDate }}
              &nbsp;
              <span :class="statusBadge(campaign.status)" class="badge">{{ campaign.status }}</span>
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-8 col-sm-12">
        <div class="d-flex justify-content-end py-3">
          <button
            v-if="!isCompleted"
            class="btn btn-sm btn-outline-primary mx-1"
            @click="$bvModal.show('add-products-modal')"
          >
            <i class="bi bi-plus-lg mr-1"></i> Add Products
          </button>
        </div>
      </div>
    </div>

    <!-- Assigned Products Table -->
    <div style="margin-bottom: 5em;">
      <b-table
        id="campaign-products-table"
        :items="assignedProducts"
        :fields="fields"
        striped
        hover
        small
        sticky-header="100vh"
        sort-icon-left
        responsive="sm"
        :busy="loadingProducts"
      >
        <template #table-busy>
          <div class="text-center py-4">
            <span class="spinner-border spinner-border-sm mr-2"></span> Loading…
          </div>
        </template>

        <template #empty>
          <div class="text-center text-muted py-4">No products assigned yet.</div>
        </template>

        <template #cell(product_name)="row">
          <div>{{ row.item.product_name }}</div>
          <small v-if="row.item.sku" class="text-muted">{{ row.item.sku }}</small>
        </template>

        <template #cell(base_ppu)="row">
          {{ convertCurrencyToLocal(row.item.base_ppu) }}
        </template>

        <template #cell(discount)="row">
          <span v-if="row.item.discount_type === 'percentage'">{{ row.item.discount_value }}%</span>
          <span v-else>{{ convertCurrencyToLocal(row.item.discount_value) }}</span>
        </template>

        <template #cell(effective_price)="row">
          <span class="text-primary font-weight-bold">{{ convertCurrencyToLocal(row.item.effective_price) }}</span>
        </template>

        <template #cell(actions)="row">
          <div class="dropdown">
            <button
              class="btn btn-outline-secondary btn-sm dropdown-toggle"
              type="button"
              data-toggle="dropdown"
              data-offset="-100,3"
            >
              Actions
            </button>
            <div class="dropdown-menu dropdown-menu-right">
              <a
                class="dropdown-item text-danger"
                href="#"
                @click.prevent="removeProduct(row.item)"
              >Remove</a>
            </div>
          </div>
        </template>
      </b-table>
    </div>

    <!-- Add Products Modal -->
    <b-modal id="add-products-modal" title="Add Products to Campaign" hide-footer size="lg" @hidden="resetAddForm">
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: addMode === 'search' }"
            href="#"
            @click.prevent="addMode = 'search'"
          >By Product</a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: addMode === 'category' }"
            href="#"
            @click.prevent="addMode = 'category'"
          >By Category</a>
        </li>
      </ul>

      <!-- By Product -->
      <div v-if="addMode === 'search'">
        <div class="form-group">
          <label>Search product</label>
          <input
            type="text"
            class="form-control"
            v-model="search"
            placeholder="Name or SKU…"
            @input="searchProducts"
          />
        </div>

        <div v-if="searchResults.length" class="list-group mb-3" style="max-height:200px;overflow-y:auto;">
          <button
            v-for="p in searchResults"
            :key="p.id"
            type="button"
            class="list-group-item list-group-item-action py-1"
            @click="selectProduct(p)"
          >
            <div class="d-flex justify-content-between">
              <span>{{ p.product }}</span>
              <small class="text-muted">{{ convertCurrencyToLocal(p.ppu) }}</small>
            </div>
            <small v-if="p.sku" class="text-muted">{{ p.sku }}</small>
          </button>
        </div>

        <div v-if="selectedProduct" class="border rounded p-2 mb-3">
          <strong>{{ selectedProduct.product }}</strong>
          <div><small class="text-muted">Base price: {{ convertCurrencyToLocal(selectedProduct.ppu) }}</small></div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Discount type</label>
            <select class="form-control" v-model="discountForm.discount_type">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed_price">Fixed promotional price</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>{{ discountForm.discount_type === 'percentage' ? 'Percentage (0–100)' : 'Promotional price' }}</label>
            <input type="number" class="form-control" v-model.number="discountForm.discount_value" min="0" step="any" />
          </div>
        </div>

        <div v-if="selectedProduct && discountForm.discount_value > 0" class="mb-3">
          <small class="text-muted">
            Promo price: <strong class="text-primary">{{ convertCurrencyToLocal(previewPrice) }}</strong>
          </small>
        </div>

        <small v-if="discountError" class="text-danger d-block mb-2">{{ discountError }}</small>

        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('add-products-modal')">Cancel</button>
          <button
            class="btn btn-primary"
            @click="addProduct"
            :disabled="!selectedProduct || saving"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
            Add to Campaign
          </button>
        </div>
      </div>

      <!-- By Category -->
      <div v-if="addMode === 'category'">
        <div class="form-group">
          <label>Select categories</label>
          <div v-if="loadingCategories" class="text-muted"><small>Loading…</small></div>
          <div v-else style="max-height:180px;overflow-y:auto;" class="border rounded p-2">
            <div v-for="cat in categories" :key="cat.id" class="custom-control custom-checkbox py-1">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'cat-' + cat.id"
                :value="cat.id"
                v-model="selectedCategoryIds"
              />
              <label class="custom-control-label" :for="'cat-' + cat.id">{{ cat.name }}</label>
            </div>
            <div v-if="!categories.length" class="text-muted"><small>No categories found.</small></div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Discount type</label>
            <select class="form-control" v-model="categoryForm.discount_type">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed_price">Fixed promotional price</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>{{ categoryForm.discount_type === 'percentage' ? 'Percentage (0–100)' : 'Promotional price' }}</label>
            <input type="number" class="form-control" v-model.number="categoryForm.discount_value" min="0" step="any" />
          </div>
        </div>

        <small class="text-muted d-block mb-3">
          All products in the selected categories will be assigned. Existing assignments will be updated.
        </small>

        <small v-if="categoryError" class="text-danger d-block mb-2">{{ categoryError }}</small>

        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('add-products-modal')">Cancel</button>
          <button
            class="btn btn-primary"
            @click="assignByCategory"
            :disabled="!selectedCategoryIds.length || categoryAssigning"
          >
            <span v-if="categoryAssigning" class="spinner-border spinner-border-sm mr-1"></span>
            Assign All ({{ selectedCategoryIds.length }} selected)
          </button>
        </div>
      </div>

    </b-modal>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DiscountCampaignProducts',
  filters: {
    shortDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
  },
  data() {
    return {
      campaignId:       window.campaignId || null,
      campaign:         null,
      assignedProducts: [],
      loadingProducts:  false,

      addMode:          'search',

      // by-product
      search:           '',
      searchResults:    [],
      searchTimer:      null,
      selectedProduct:  null,
      discountForm:     { discount_type: 'percentage', discount_value: '' },
      discountError:    '',
      saving:           false,

      // by-category
      categories:           [],
      loadingCategories:    false,
      selectedCategoryIds:  [],
      categoryForm:         { discount_type: 'percentage', discount_value: '' },
      categoryError:        '',
      categoryAssigning:    false,

      fields: [
        { key: 'product_name', label: 'Product',     sortable: true },
        { key: 'base_ppu',     label: 'Base Price',  sortable: true },
        { key: 'discount',     label: 'Discount' },
        { key: 'effective_price', label: 'Promo Price', sortable: true },
        { key: 'actions',      label: '',            thStyle: { width: '100px' } },
      ],
    };
  },
  computed: {
    isCompleted() {
      return this.campaign?.status === 'completed';
    },
    previewPrice() {
      if (!this.selectedProduct || !this.discountForm.discount_value) return 0;
      const base = parseFloat(this.selectedProduct.ppu) || 0;
      const val  = parseFloat(this.discountForm.discount_value) || 0;
      if (this.discountForm.discount_type === 'fixed_price') return val;
      return Math.round(base * (1 - val / 100) * 100) / 100;
    },
  },
  mounted() {
    if (this.campaignId) {
      this.fetchCampaign();
      this.fetchAssigned();
      this.fetchCategories();
    }
  },
  methods: {
    async fetchCampaign() {
      try {
        const { data } = await axios.get(`/api/discount-campaigns/${this.campaignId}`);
        this.campaign = data;
      } catch { /* ignore */ }
    },

    async fetchAssigned() {
      this.loadingProducts = true;
      try {
        const { data } = await axios.get(`/api/discount-campaigns/${this.campaignId}/products`);
        this.assignedProducts = data;
      } catch { /* ignore */ } finally {
        this.loadingProducts = false;
      }
    },

    async fetchCategories() {
      this.loadingCategories = true;
      try {
        const { data } = await axios.get('/api/items-category');
        this.categories = Array.isArray(data) ? data : Object.values(data);
      } catch { /* ignore */ } finally {
        this.loadingCategories = false;
      }
    },

    searchProducts() {
      clearTimeout(this.searchTimer);
      if ((this.search || '').trim().length < 2) { this.searchResults = []; return; }
      this.searchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/products', { params: { search: this.search, per_page: 15 } });
          this.searchResults = data.items || data.data || data;
        } catch { this.searchResults = []; }
      }, 300);
    },

    selectProduct(product) {
      this.selectedProduct             = product;
      this.search                      = product.product;
      this.searchResults               = [];
      this.discountForm.discount_value = '';
      this.discountError               = '';
    },

    async addProduct() {
      this.discountError = '';
      if (!this.selectedProduct) return;
      const val = parseFloat(this.discountForm.discount_value);
      if (!val || val <= 0) { this.discountError = 'Enter a valid discount value.'; return; }
      if (this.discountForm.discount_type === 'percentage' && val > 100) {
        this.discountError = 'Percentage cannot exceed 100.'; return;
      }
      this.saving = true;
      try {
        await axios.post(`/api/discount-campaigns/${this.campaignId}/products`, {
          products: [{ product_id: this.selectedProduct.id, discount_type: this.discountForm.discount_type, discount_value: val }],
        });
        this.selectedProduct             = null;
        this.search                      = '';
        this.discountForm.discount_value = '';
        this.$bvModal.hide('add-products-modal');
        await this.fetchAssigned();
      } catch (err) {
        this.discountError = err.response?.data?.message || 'Could not add product.';
      } finally { this.saving = false; }
    },

    async assignByCategory() {
      this.categoryError = '';
      const val = parseFloat(this.categoryForm.discount_value);
      if (!val || val <= 0) { this.categoryError = 'Enter a valid discount value.'; return; }
      if (this.categoryForm.discount_type === 'percentage' && val > 100) {
        this.categoryError = 'Percentage cannot exceed 100.'; return;
      }
      this.categoryAssigning = true;
      try {
        const { data } = await axios.post(`/api/discount-campaigns/${this.campaignId}/products/by-category`, {
          category_ids:   this.selectedCategoryIds,
          discount_type:  this.categoryForm.discount_type,
          discount_value: val,
        });
        this.selectedCategoryIds         = [];
        this.categoryForm.discount_value = '';
        this.$bvModal.hide('add-products-modal');
        await this.fetchAssigned();
        alert(`${data.count} product(s) assigned.`);
      } catch (err) {
        this.categoryError = err.response?.data?.message || 'Could not assign by category.';
      } finally { this.categoryAssigning = false; }
    },

    async removeProduct(item) {
      if (!confirm(`Remove "${item.product_name}" from this campaign?`)) return;
      try {
        await axios.delete(`/api/discount-campaigns/${this.campaignId}/products/${item.product_id}`);
        await this.fetchAssigned();
      } catch (err) {
        alert(err.response?.data?.message || 'Could not remove product.');
      }
    },

    resetAddForm() {
      this.search                      = '';
      this.searchResults               = [];
      this.selectedProduct             = null;
      this.discountForm                = { discount_type: 'percentage', discount_value: '' };
      this.discountError               = '';
      this.selectedCategoryIds         = [];
      this.categoryForm                = { discount_type: 'percentage', discount_value: '' };
      this.categoryError               = '';
    },

    statusBadge(status) {
      const map = { active: 'badge-primary', scheduled: 'badge-secondary', paused: 'badge-dark', completed: 'badge-secondary' };
      return map[status] || 'badge-secondary';
    },
  },
};
</script>
